export const M4_UNIT_12 = {
  id: "m4-u12-weather",
  title: "Unidad 12: What's the Weather Like? / El Clima",
  topic: "Master weather conversations! Learn to describe weather, temperature, and how it affects your activities, clothes, and feelings. Essential for daily conversations in Canada where weather changes everything!",
  iconType: "weather",
  grammarImage: "/grammar/grammar-m4-u12.jpg",
  grammarText: "IT IS/IT'S + weather adjective (It's sunny, It's raining). IT IS + temperature (It's 20 degrees, It's hot, It's freezing). TOO + adjective + TO + verb (It's too cold to run). I'M + feeling + WHEN + weather (I'm happy when it's sunny). BECAUSE + weather (I'm wearing a jacket because it's cold).",
  
  sets: [
    // SET 1: Basic Weather Vocabulary
    {
      id: "m4-u12-set1",
      title: "Set 1: Weather Conditions",
      questions: [
        {
          type: "match",
          q: "Match weather word to Spanish:",
          pairs: [
            { left: "sunny", right: "soleado" },
            { left: "rainy", right: "lluvioso" },
            { left: "cloudy", right: "nublado" }
          ],
          a: "completed",
          tip: "These are THE most common weather conditions! Add -y to make adjective: sun → sunny, rain → rainy!"
        },
        {
          type: "mc",
          q: "'It's ___ today.' (soleado)",
          options: ["sunny", "sun", "sunning"],
          a: "sunny",
          tip: "Use adjective! 'It's sunny' ✓ not 'It's sun' ❌. IT + IS + adjective!"
        },
        {
          type: "input",
          q: "Complete: 'It's ___ outside.' (lluvioso)",
          a: "rainy"
        },
        {
          type: "order",
          q: "Ordena: 'Está nublado hoy'",
          words: ["It's", "cloudy", "today."],
          a: "It's cloudy today."
        },
        {
          type: "mc",
          q: "Structure: 'It ___ sunny.' (Which verb?)",
          options: ["is", "am", "are"],
          a: "is",
          tip: "IT + IS! Weather always uses 'IT'! 'It is/It's' ✓ Never 'He is sunny' ❌"
        },
        {
          type: "input",
          q: "Complete: 'It's ___ today.' (ventoso - windy)",
          a: "windy"
        },
        {
          type: "mc",
          q: "'It's raining' uses continuous because:",
          options: [
            "It's happening right now",
            "It's a routine",
            "It's wrong"
          ],
          a: "It's happening right now",
          successtip: "✓ IT'S RAINING! Now action! 🌧️ 'It's raining' = está lloviendo (now). 'It rains' = llueve (in general). Different!"
        },
        {
          type: "order",
          q: "Ordena: 'Está nevando'",
          words: ["It's", "snowing."],
          a: "It's snowing.",
          successtip: "✓ IT'S SNOWING! Canadian life! ❄️ This is THE phrase you'll use Oct-April in Canada! Essential!"
        },
        {
          type: "input",
          q: "Complete: 'It ___ cold today.' (is/are)",
          a: "is"
        },
        {
          type: "mc",
          q: "ERROR: 'Is sunny today' - Correction:",
          options: [
            "'It's sunny today' (need IT)",
            "'Is sunny today' is correct",
            "'Sunny is today'"
          ],
          a: "'It's sunny today' (need IT)",
          tip: "Must include IT! 'IT is sunny' ✓ Can't say just 'Is sunny' ❌ in English!"
        }
      ]
    },

    // SET 2: Asking About Weather
    {
      id: "m4-u12-set2",
      title: "Set 2: 'What's the Weather Like?'",
      questions: [
        {
          type: "match",
          q: "Match question to answer:",
          pairs: [
            { left: "What's the weather like?", right: "It's sunny!" },
            { left: "How's the weather?", right: "It's cold." },
            { left: "Is it raining?", right: "Yes, it is." }
          ],
          a: "completed",
          tip: "Two ways to ask! 'What's the weather LIKE?' = 'How's the weather?' = same meaning!"
        },
        {
          type: "mc",
          q: "Most common way to ask about weather:",
          options: [
            "'What's the weather like?'",
            "'How is weather?'",
            "'Is it weather?'"
          ],
          a: "'What's the weather like?'",
          tip: "'LIKE' at end! 'What's the weather LIKE?' ✓ This is #1 most common weather question!"
        },
        {
          type: "input",
          q: "Complete: '___ the weather like today?' (What's/How's)",
          a: "What's / How's"
        },
        {
          type: "order",
          q: "Ordena: '¿Cómo está el clima?'",
          words: ["What's", "the", "weather", "like?"],
          a: "What's the weather like?"
        },
        {
          type: "mc",
          q: "Short answer to 'Is it raining?' - 'Yes, ___.'",
          options: ["it is", "is it", "it raining"],
          a: "it is",
          successtip: "✓ SHORT ANSWERS! Quick responses! 💬 'Yes, it is' or 'No, it isn't'. Don't repeat whole sentence!"
        },
        {
          type: "input",
          q: "Complete: 'Is ___ snowing?' (it/he)",
          a: "it"
        },
        {
          type: "mc",
          q: "'How's the weather?' - You answer:",
          options: [
            "Describe condition: 'It's sunny' or 'It's cold'",
            "'Yes'",
            "'The weather is good'"
          ],
          a: "Describe condition: 'It's sunny' or 'It's cold'",
          tip: "Describe directly! 'It's sunny/rainy/cold/hot' etc. Give actual weather!"
        },
        {
          type: "order",
          q: "Ordena: '¿Está frío afuera?'",
          words: ["Is", "it", "cold", "outside?"],
          a: "Is it cold outside?"
        },
        {
          type: "input",
          q: "Complete: 'What's it ___ outside?' (like = how is it)",
          a: "like"
        },
        {
          type: "mc",
          q: "Canadian small talk: First thing people often say:",
          options: [
            "'What's the weather like today?' or 'Cold enough for you?'",
            "'How old are you?'",
            "'How much money do you make?'"
          ],
          a: "'What's the weather like today?' or 'Cold enough for you?'",
          tip: "Weather = #1 small talk topic in Canada! Everyone talks about weather. Safe, polite topic!"
        }
      ]
    },

    // SET 3: Temperature Vocabulary
    {
      id: "m4-u12-set3",
      title: "Set 3: Hot, Cold & Temperature",
      questions: [
        {
          type: "match",
          q: "Match temperature word to Spanish:",
          pairs: [
            { left: "hot", right: "caliente/caluroso" },
            { left: "warm", right: "templado/cálido" },
            { left: "cold", right: "frío" }
          ],
          a: "completed",
          tip: "Temperature scale: freezing → cold → cool → warm → hot → boiling!"
        },
        {
          type: "mc",
          q: "'It's ___ today - 30 degrees!' (muy caliente)",
          options: ["hot", "warm", "cold"],
          a: "hot",
          tip: "Hot = caliente (30°C+). Warm = templado (15-25°C). Know the difference!"
        },
        {
          type: "input",
          q: "Complete: 'It's ___ outside.' (frío)",
          a: "cold"
        },
        {
          type: "order",
          q: "Ordena: 'Está muy frío hoy'",
          words: ["It's", "very", "cold", "today."],
          a: "It's very cold today."
        },
        {
          type: "mc",
          q: "Emphasis for VERY cold: 'It's ___!'",
          options: ["freezing (coloquial = muy frío)", "cold", "frozen"],
          a: "freezing (coloquial = muy frío)",
          successtip: "✓ FREEZING! Exaggeration! 🥶 'It's freezing!' = está helado/congelando (no literal). Common in Canada! -5°C = 'freezing'!"
        },
        {
          type: "input",
          q: "Complete: 'It's ___ today - perfect weather!' (templado - warm)",
          a: "warm"
        },
        {
          type: "mc",
          q: "Emphasis for VERY hot: 'It's ___!'",
          options: ["boiling (coloquial = muy caliente)", "hot", "boiled"],
          a: "boiling (coloquial = muy caliente)",
          tip: "'It's boiling!' = hace un calor terrible (exaggeration). 'Boiling' = hirviendo (used for emphasis)!"
        },
        {
          type: "order",
          q: "Ordena: 'Está helado afuera - necesito abrigo'",
          words: ["It's", "freezing", "outside", "-", "I", "need", "a", "coat."],
          a: "It's freezing outside - I need a coat.",
          successtip: "✓ CONNECTING IDEAS! Weather + action! 🧥 Weather affects what you do! This is natural speech pattern!"
        },
        {
          type: "input",
          q: "Complete: 'It's 25 degrees. It's ___!' (nice/warm)",
          a: "warm / nice"
        },
        {
          type: "mc",
          q: "'It's minus 20 degrees' = It's ___.",
          options: ["freezing / extremely cold", "warm", "hot"],
          a: "freezing / extremely cold",
          tip: "Minus = below zero = negative temperature! -20°C = VERY cold! Common in Canadian winter!"
        }
      ]
    },

    // SET 4: Weather + Clothes Connection
    {
      id: "m4-u12-set4",
      title: "Set 4: Weather & What to Wear",
      questions: [
        {
          type: "match",
          q: "Match weather to clothing:",
          pairs: [
            { left: "It's cold", right: "I'm wearing a coat" },
            { left: "It's hot", right: "I'm wearing shorts" },
            { left: "It's raining", right: "I need an umbrella" }
          ],
          a: "completed",
          tip: "Weather determines clothes! Natural connection in conversation!"
        },
        {
          type: "mc",
          q: "'It's snowing, so I'm wearing ___.'",
          options: ["a winter coat and boots", "shorts and t-shirt", "a swimsuit"],
          a: "a winter coat and boots",
          tip: "SO = therefore/por eso. Weather SO clothing = cause and effect!"
        },
        {
          type: "input",
          q: "Complete: 'It's raining. I need an ___.' (paraguas)",
          a: "umbrella"
        },
        {
          type: "order",
          q: "Ordena: 'Hace calor, estoy usando shorts'",
          words: ["It's", "hot,", "so", "I'm", "wearing", "shorts."],
          a: "It's hot, so I'm wearing shorts."
        },
        {
          type: "mc",
          q: "'I'm wearing a jacket ___ it's cold.'",
          options: ["because", "but", "when"],
          a: "because",
          successtip: "✓ BECAUSE! Reason! 💡 'Because' = porque (explains WHY). Weather = reason for clothing choice!"
        },
        {
          type: "input",
          q: "Complete: 'It's sunny. I'm wearing ___.' (lentes de sol)",
          a: "sunglasses"
        },
        {
          type: "mc",
          q: "'It's freezing! You should wear ___.'",
          options: ["a warm coat and gloves", "a t-shirt", "sandals"],
          a: "a warm coat and gloves",
          tip: "Should = deberías (advice). Weather advice = very common in Canada!"
        },
        {
          type: "order",
          q: "Ordena: 'Está nevando, necesito mis botas'",
          words: ["It's", "snowing,", "so", "I", "need", "my", "boots."],
          a: "It's snowing, so I need my boots.",
          successtip: "✓ SNOW BOOTS! Essential gear! 👢 Canada = need winter boots Oct-Apr! Practical advice!"
        },
        {
          type: "input",
          q: "Complete: 'It's cloudy, but it's ___ raining, so I don't need an umbrella.' (not)",
          a: "not"
        },
        {
          type: "mc",
          q: "Canadian winter essential: 'It's -15°C. I'm wearing ___.'",
          options: ["a winter coat, hat, scarf, and gloves", "just a sweater", "summer clothes"],
          a: "a winter coat, hat, scarf, and gloves",
          tip: "Full winter gear! Coat, hat, scarf, gloves, warm boots = essential Canadian winter survival!"
        }
      ]
    },

    // SET 5: Weather + Activities Connection
    {
      id: "m4-u12-set5",
      title: "Set 5: Weather & Activities",
      questions: [
        {
          type: "match",
          q: "Match weather to activity:",
          pairs: [
            { left: "It's sunny and warm", right: "We're going to the park" },
            { left: "It's raining", right: "We're staying home" },
            { left: "It's snowing", right: "Kids are playing outside" }
          ],
          a: "completed",
          tip: "Weather controls activities! What you can/can't do based on weather!"
        },
        {
          type: "mc",
          q: "'It's sunny today, so we're ___ to the beach.'",
          options: ["going", "go", "goes"],
          a: "going",
          tip: "Weather SO activity! Present continuous for planned activity happening today!"
        },
        {
          type: "input",
          q: "Complete: 'It's raining, so we're ___ home.' (staying/going)",
          a: "staying"
        },
        {
          type: "order",
          q: "Ordena: 'Está nevando, los niños están jugando afuera'",
          words: ["It's", "snowing,", "so", "the", "kids", "are", "playing", "outside."],
          a: "It's snowing, so the kids are playing outside."
        },
        {
          type: "mc",
          q: "'It's too cold ___ run outside.'",
          options: ["to", "for", "at"],
          a: "to",
          successtip: "✓ TOO + adjective + TO + verb! Structure! 🏃 'Too cold TO run' = demasiado frío PARA correr. Key pattern!"
        },
        {
          type: "input",
          q: "Complete: 'It's too hot ___ work outside.' (to/for)",
          a: "to"
        },
        {
          type: "mc",
          q: "'The weather is perfect, so we're ___.'",
          options: ["having a picnic (present continuous - now plan)", "have a picnic (simple)", "has a picnic"],
          a: "having a picnic (present continuous - now plan)",
          tip: "Perfect weather = time for outdoor activities! Picnic, BBQ, park = Canadian summer!"
        },
        {
          type: "order",
          q: "Ordena: 'Está lloviendo demasiado para caminar'",
          words: ["It's", "raining", "too", "much", "to", "walk."],
          a: "It's raining too much to walk.",
          successtip: "✓ TOO MUCH! Quantity + activity! ☔ 'Too much' = demasiado. Can't do activity because of weather!"
        },
        {
          type: "input",
          q: "Complete: 'It's windy, so we're ___ flying kites.' (present continuous)",
          a: "flying"
        },
        {
          type: "mc",
          q: "Canadian winter activity: 'It's snowing! Let's go ___.'",
          options: ["skating / skiing / sledding", "swimming", "to the beach"],
          a: "skating / skiing / sledding",
          tip: "Snow = winter sports! Skating, skiing, sledding, snowboarding = Canadian winter fun!"
        }
      ]
    },

    // SET 6: Weather + Feelings Connection
    {
      id: "m4-u12-set6",
      title: "Set 6: Weather & How You Feel",
      questions: [
        {
          type: "match",
          q: "Match weather to feeling:",
          pairs: [
            { left: "When it's sunny", right: "I'm happy" },
            { left: "When it's gray and rainy", right: "I feel sad" },
            { left: "When it snows", right: "I'm excited" }
          ],
          a: "completed",
          tip: "Weather affects mood! Very common to connect weather to emotions!"
        },
        {
          type: "mc",
          q: "'I'm happy ___ it's sunny.'",
          options: ["when", "because", "both correct!"],
          a: "both correct!",
          tip: "'When' OR 'because' both work! 'When it's sunny' = cuando hace sol. 'Because it's sunny' = porque hace sol!"
        },
        {
          type: "input",
          q: "Complete: 'I feel sad ___ it's gray and rainy.' (when/because)",
          a: "when / because"
        },
        {
          type: "order",
          q: "Ordena: 'Me siento feliz cuando hace sol'",
          words: ["I", "feel", "happy", "when", "it's", "sunny."],
          a: "I feel happy when it's sunny."
        },
        {
          type: "mc",
          q: "'I love it when ___.'",
          options: ["it snows (winter lovers)", "it's raining all day", "it's freezing"],
          a: "it snows (winter lovers)",
          successtip: "✓ 'I LOVE IT WHEN'! Expression! ❤️ 'I love it when...' = me encanta cuando... Common feeling phrase!"
        },
        {
          type: "input",
          q: "Complete: 'I hate it ___ it's too hot.' (when/because)",
          a: "when"
        },
        {
          type: "mc",
          q: "'When it's cold and dark, I feel ___.'",
          options: ["tired / depressed / sad", "happy", "excited"],
          a: "tired / depressed / sad",
          tip: "SAD (Seasonal Affective Disorder) = real in Canada! Long, dark, cold winters affect mood!"
        },
        {
          type: "order",
          q: "Ordena: 'Me encanta cuando nieva - es hermoso'",
          words: ["I", "love", "it", "when", "it", "snows", "-", "it's", "beautiful."],
          a: "I love it when it snows - it's beautiful.",
          successtip: "✓ MIXING FEELINGS + OPINIONS! Natural! 🌨️ Weather feeling + weather opinion = complete emotional response!"
        },
        {
          type: "input",
          q: "Complete: 'I'm ___ when it's warm and sunny.' (happy/sad)",
          a: "happy"
        },
        {
          type: "mc",
          q: "Canadian reality: 'I'm tired of winter. It's ___ for too long.'",
          options: ["cold / dark / snowing (ongoing - continuous)", "cold", "was cold"],
          a: "cold / dark / snowing (ongoing - continuous)",
          tip: "'Tired of' = harto de. Canadian winter fatigue = REAL! March = everyone tired of cold/snow!"
        }
      ]
    },

    // SET 7: Seasons & Weather Patterns
    {
      id: "m4-u12-set7",
      title: "Set 7: Seasons in Canada",
      questions: [
        {
          type: "match",
          q: "Match season to typical weather:",
          pairs: [
            { left: "Summer", right: "Hot and sunny" },
            { left: "Winter", right: "Cold and snowy" },
            { left: "Fall/Autumn", right: "Cool and colorful leaves" }
          ],
          a: "completed",
          tip: "Canada has FOUR distinct seasons! Each with very different weather!"
        },
        {
          type: "mc",
          q: "'In summer, it's usually ___.'",
          options: ["hot and sunny", "cold and snowy", "freezing"],
          a: "hot and sunny",
          tip: "Canadian summer = June, July, August. Usually hot (25-35°C). But can vary by province!"
        },
        {
          type: "input",
          q: "Complete: 'In winter, it ___ a lot.' (snow - simple for routine)",
          a: "snows"
        },
        {
          type: "order",
          q: "Ordena: 'En otoño, las hojas cambian de color'",
          words: ["In", "fall,", "the", "leaves", "change", "color."],
          a: "In fall, the leaves change color.",
          successtip: "✓ FALL! Beautiful season! 🍂 Fall = otoño (US/Canada). Autumn = UK. Leaves = hojas. Oct-Nov = gorgeous!"
        },
        {
          type: "mc",
          q: "'Spring in Canada is ___.'",
          options: ["warm and rainy", "hot and dry", "cold and snowy"],
          a: "warm and rainy",
          tip: "Spring = April, May. Snow melts, flowers bloom, lots of rain! Temperature rising!"
        },
        {
          type: "input",
          q: "Complete: 'In ___, it's freezing and we have snow.' (winter)",
          a: "winter"
        },
        {
          type: "mc",
          q: "Canadian winter months:",
          options: ["December, January, February, (+ March often!)", "June, July, August", "September, October, November"],
          a: "December, January, February, (+ March often!)",
          successtip: "✓ LONG WINTER! Reality! 🥶 Winter = Dec-March (sometimes April!). 4-5 months of cold! Prepare mentally!"
        },
        {
          type: "order",
          q: "Ordena: 'En verano, vamos a la playa todos los fines de semana'",
          words: ["In", "summer,", "we", "go", "to", "the", "beach", "every", "weekend."],
          a: "In summer, we go to the beach every weekend."
        },
        {
          type: "input",
          q: "Complete: 'I love ___ because the weather is perfect.' (spring/summer)",
          a: "spring / summer"
        },
        {
          type: "mc",
          q: "'Right now it's winter, so ___.'",
          options: [
            "it's cold and I'm wearing my winter coat",
            "it's hot and I'm at the beach",
            "it's warm and I'm wearing shorts"
          ],
          a: "it's cold and I'm wearing my winter coat",
          tip: "NOW = continuous! Current season = current weather = current clothes! Connect all three!"
        }
      ]
    },

    // SET 8: Weather Expressions & Idioms
    {
      id: "m4-u12-set8",
      title: "Set 8: Common Weather Phrases",
      questions: [
        {
          type: "mc",
          q: "'It's raining cats and dogs' means:",
          options: [
            "It's raining very hard (llueve a cántaros)",
            "Animals are falling from sky",
            "It's not raining"
          ],
          a: "It's raining very hard (llueve a cántaros)",
          tip: "Idiom! 'Raining cats and dogs' = raining VERY hard. Fun expression but common!"
        },
        {
          type: "input",
          q: "Complete: 'It looks ___ rain.' (like it might rain)",
          a: "like"
        },
        {
          type: "order",
          q: "Ordena: 'Parece que va a llover'",
          words: ["It", "looks", "like", "it's", "going", "to", "rain."],
          a: "It looks like it's going to rain.",
          successtip: "✓ PREDICTION! Weather forecast! ☁️ 'It looks like' = parece que. Looking at sky to predict weather!"
        },
        {
          type: "mc",
          q: "'Bundle up!' means:",
          options: [
            "Wear warm clothes (abrígate bien)",
            "Take off your coat",
            "Go outside"
          ],
          a: "Wear warm clothes (abrígate bien)",
          tip: "'Bundle up' = abrígate bien (dress warmly). Common Canadian advice in winter!"
        },
        {
          type: "input",
          q: "Complete: 'Bring an umbrella. I think it's ___ to rain.' (going)",
          a: "going"
        },
        {
          type: "mc",
          q: "'What a beautiful day!' means:",
          options: [
            "The weather is perfect (¡qué día tan hermoso!)",
            "Today is your birthday",
            "It's raining"
          ],
          a: "The weather is perfect (¡qué día tan hermoso!)",
          successtip: "✓ EXCLAMATIONS! Natural reactions! ☀️ 'What a...' = ¡qué...! Express excitement about good weather!"
        },
        {
          type: "order",
          q: "Ordena: '¡Qué día tan horrible! - está lloviendo todo el día'",
          words: ["What", "a", "terrible", "day!", "It's", "raining", "all", "day."],
          a: "What a terrible day! It's raining all day."
        },
        {
          type: "input",
          q: "Complete: 'Cold enough ___ you?' (for - Canadian small talk)",
          a: "for"
        },
        {
          type: "mc",
          q: "'At least it's not raining!' means:",
          options: [
            "Positive thinking (por lo menos no llueve)",
            "It's definitely raining",
            "I want rain"
          ],
          a: "Positive thinking (por lo menos no llueve)",
          tip: "'At least' = por lo menos. Finding positive in bad weather = Canadian optimism!"
        },
        {
          type: "mc",
          q: "Canadian greeting: 'How about this weather?' expects:",
          options: [
            "Weather comment/complaint (small talk starter)",
            "A number",
            "No response"
          ],
          a: "Weather comment/complaint (small talk starter)",
          tip: "'How about this weather?' = isn't this weather crazy? Rhetorical question = small talk starter!"
        }
      ]
    },

    // SET 9: Real Weather Situations
    {
      id: "m4-u12-set9",
      title: "Set 9: Weather Planning & Decisions",
      questions: [
        {
          type: "mc",
          q: "Planning: 'What's the weather like tomorrow?' - 'The forecast says ___.'",
          options: [
            "it's going to be sunny and warm",
            "it is sunny yesterday",
            "it was cold"
          ],
          a: "it's going to be sunny and warm",
          tip: "Forecast = pronóstico. Use 'going to' for weather predictions! Future weather plans!"
        },
        {
          type: "input",
          q: "Complete: 'If it's raining tomorrow, we ___ stay home.' (will/won't)",
          a: "will"
        },
        {
          type: "order",
          q: "Ordena: 'Si hace buen tiempo mañana, iremos al parque'",
          words: ["If", "the", "weather", "is", "nice", "tomorrow,", "we'll", "go", "to", "the", "park."],
          a: "If the weather is nice tomorrow, we'll go to the park.",
          successtip: "✓ CONDITIONAL! Planning based on weather! 🤔 IF + weather + WILL + activity. Essential planning structure!"
        },
        {
          type: "mc",
          q: "'Should I bring a jacket?' - 'Yes, ___.'",
          options: [
            "it's getting cold later",
            "it was cold yesterday",
            "you are cold"
          ],
          a: "it's getting cold later",
          tip: "'Should I bring...?' = common question when going out! Weather affects what to bring!"
        },
        {
          type: "input",
          q: "Complete: 'Let's check the ___ before we leave.' (forecast = pronóstico)",
          a: "forecast / weather"
        },
        {
          type: "mc",
          q: "Weather app says rain. You say: '___'",
          options: [
            "'I'd better take an umbrella'",
            "'I don't need anything'",
            "'Let's go swimming'"
          ],
          a: "'I'd better take an umbrella'",
          successtip: "✓ 'I'D BETTER'! Recommendation! ☂️ 'I'd better' = mejor que... (self-advice). Weather determines preparation!"
        },
        {
          type: "order",
          q: "Ordena: 'Cancelemos el picnic - dice que va a llover todo el día'",
          words: ["Let's", "cancel", "the", "picnic", "-", "it's", "going", "to", "rain", "all", "day."],
          a: "Let's cancel the picnic - it's going to rain all day."
        },
        {
          type: "input",
          q: "Complete: 'The weather is perfect ___ a BBQ!' (for)",
          a: "for"
        },
        {
          type: "mc",
          q: "'It's supposed to snow tonight' means:",
          options: [
            "The forecast predicts snow (se supone que nevará)",
            "It's snowing now",
            "It snowed yesterday"
          ],
          a: "The forecast predicts snow (se supone que nevará)",
          tip: "'Supposed to' = se supone que (based on forecast). Weather prediction phrase!"
        },
        {
          type: "mc",
          q: "Canadian winter driving: 'It's snowing heavily. You should ___.'",
          options: [
            "drive carefully / slowly OR stay home",
            "drive fast",
            "not wear your seatbelt"
          ],
          a: "drive carefully / slowly OR stay home",
          tip: "Snow = dangerous driving! 'Drive carefully' OR 'stay home' = safe advice. Many Canadians work from home in snowstorms!"
        }
      ]
    },

    // SET 10: Weather Mastery - Complex Situations
    {
      id: "m4-u12-set10",
      title: "Set 10: Advanced Weather Conversations",
      questions: [
        {
          type: "mc",
          q: "'I can't believe this weather! Yesterday it was 20 degrees and sunny. Today ___.'",
          options: [
            "it's only 5 degrees and raining!",
            "it was cold",
            "it is 20 degrees"
          ],
          a: "it's only 5 degrees and raining!",
          tip: "Weather changes FAST in Canada! Compare yesterday (past) vs today (present)!"
        },
        {
          type: "input",
          q: "Complete: 'I usually love winter, but this year it's ___ too cold and long.' (being - temporary complaint)",
          a: "being"
        },
        {
          type: "order",
          q: "Ordena: 'Generalmente me gusta el invierno, pero este año está siendo demasiado frío'",
          words: ["I", "usually", "like", "winter,", "but", "this", "year", "it's", "being", "too", "cold."],
          a: "I usually like winter, but this year it's being too cold.",
          successtip: "✓ ROUTINE VS EXCEPTION! Advanced! 🌨️ 'Usually like' (simple) BUT 'this year is being' (continuous exception). Mixing tenses = fluency!"
        },
        {
          type: "mc",
          q: "'The weather is so unpredictable! One minute it's sunny, ___.'",
          options: [
            "the next minute it's raining",
            "yesterday was cold",
            "it will be hot"
          ],
          a: "the next minute it's raining",
          tip: "Unpredictable = impredecible. Fast changes = 'one minute...the next minute' = common phrase!"
        },
        {
          type: "input",
          q: "Complete: 'I'm so tired ___ this cold weather.' (of = harto de)",
          a: "of"
        },
        {
          type: "mc",
          q: "'I don't mind the cold, but I hate ___.'",
          options: [
            "when it's dark all the time (winter days)",
            "when it's sunny",
            "summer"
          ],
          a: "when it's dark all the time (winter days)",
          successtip: "✓ WINTER DARKNESS! Real issue! 🌑 Short days (8am sunrise, 4:30pm sunset Dec-Feb) = hardest part of Canadian winter!"
        },
        {
          type: "order",
          q: "Ordena: 'No me importa el frío, pero odio cuando está oscuro todo el tiempo'",
          words: ["I", "don't", "mind", "the", "cold,", "but", "I", "hate", "when", "it's", "dark", "all", "the", "time."],
          a: "I don't mind the cold, but I hate when it's dark all the time."
        },
        {
          type: "input",
          q: "Complete: 'They say we're ___ to have a warm spring this year.' (going)",
          a: "going"
        },
        {
          type: "mc",
          q: "'Right now it's snowing, so the kids are playing outside, but normally when it snows, ___.'",
          options: [
            "they stay inside because it's too cold",
            "they always play outside",
            "it's sunny"
          ],
          a: "they stay inside because it's too cold",
          tip: "Mixing NOW (continuous) vs NORMALLY (simple)! Complex situation with weather + exception!"
        },
        {
          type: "mc",
          q: "Complete conversation: A: 'What's the weather like?' B: 'Right now it's sunny, but the forecast says ___.' A: 'Then ___.' B: 'Good idea!'",
          options: [
            "B: 'it's going to rain later' | A: 'we'd better take umbrellas'",
            "B: 'it was raining yesterday' | A: 'let's go swimming'",
            "B: 'it's sunny' | A: 'I hate sun'"
          ],
          a: "B: 'it's going to rain later' | A: 'we'd better take umbrellas'",
          successtip: "✓ NATURAL CONVERSATION! Complete! 🌤️→🌧️ NOW + FORECAST + PLANNING. This is exactly how Canadians talk about weather!"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: 'Hi! How are you?' 'I'm good, but I'm so tired of this weather! It's been cold and gray for weeks. I know winter is long in Canada, but this year it's being extra cold. Yesterday it was minus 15 degrees! Right now it's minus 10 and snowing again. I usually don't mind winter - I love it when it snows at Christmas - but in March, I'm ready for spring! The forecast says it's going to warm up next week, so I'm excited. When it's warm and sunny, I feel so much happier. I can't wait to go to the park without wearing three layers of clothes!'\n\nThis paragraph uses:",
          options: [
            "EVERYTHING! Present simple (I know, I don't mind), continuous (it's being, it's snowing), past (it was), future (going to), weather + feelings, weather + clothes, weather + activities = COMPLETE mastery!",
            "Only simple present",
            "Only continuous"
          ],
          a: "EVERYTHING! Present simple (I know, I don't mind), continuous (it's being, it's snowing), past (it was), future (going to), weather + feelings, weather + clothes, weather + activities = COMPLETE mastery!",
          successtip: "✓ UNIT 12 MASTERED! Weather fluency complete! 🎉 From basic 'It's sunny' to complex weather conversations! You can now: describe weather, ask about weather, connect weather to clothes/activities/feelings, understand forecasts, make plans based on weather, express preferences, use idioms, handle Canadian small talk! This is ESSENTIAL Canadian conversation skill! Weather = #1 topic! You sound like a local! 🇨🇦☀️🌧️❄️"
        }
      ]
    }
  ],

  // ===================================================================
// READING SET: Ana's First Canadian Winter (Weather & Adaptation)
// ===================================================================
readingSet: {
  id: "m4-u12-reading",
  title: "Reading: Surviving the Canadian Seasons",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Fall - The Honeymoon Phase (preguntas 1, 2, 3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Beautiful Beginning**

Hi! I'm Ana from Mexico, and I want to tell you about my first year experiencing Canadian weather. It's **SO** different from home!

I arrived in September, and everyone said, "Fall is beautiful in Canada!" They were right! The weather in September and October was perfect - warm and sunny during the day, cool in the evening. The leaves changed color - red, orange, yellow - so beautiful! I wore light jackets and felt great.`
    },
    {
      type: "mc",
      q: "¿Cuántas palabras relacionadas con el clima (weather words) utiliza Ana?",
      options: [
        "Muchas (sunny, rainy, snowing, freezing, warm, cold, etc.)",
        "Solo 2 o 3",
        "Ninguna"
      ],
      a: "Muchas (sunny, rainy, snowing, freezing, warm, cold, etc.)",
      successtip: "✓ VOCABULARY!\nAna usa una gran variedad de adjetivos para describir cada estación. 🌡️"
    },
    {
      type: "mc",
      q: "En la frase 'It's SO different', ¿por qué Ana escribe 'SO' en mayúsculas?",
      options: [
        "Para enfatizar QUÉ TAN diferente es (énfasis emocional)",
        "Es un error de gramática",
        "Significa que es 'más o menos' (so-so) diferente"
      ],
      a: "Para enfatizar QUÉ TAN diferente es (énfasis emocional)",
      successtip: "✓ EMPHASIS!\n'SO' + adjetivo es la forma más común de dar énfasis en inglés hablado. 💥"
    },
    {
      type: "mc",
      q: "En 'The leaves changed color', ¿en qué tiempo verbal está 'changed'?",
      options: [
        "Pasado simple (sucedió en el otoño pasado)",
        "Presente simple",
        "Futuro"
      ],
      a: "Pasado simple (sucedió en el otoño pasado)",
      successtip: "✓ PAST TENSE!\nElla está narrando su experiencia cronológicamente. 🍂"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Cold Reality (preguntas 4, 5, 6, 7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Arrival of Winter**

By January, it was minus 20 degrees! In Mexico, we don't have minus temperatures! I was freezing **ALL** the time, even though I was wearing 10 layers of clothes. 

My coworkers kept saying, "Cold enough for you?" At first I didn't understand why Canadians talk about weather so much. Now I do! It's too cold to walk to work, so I take the bus. It's too icy to drive, so many people work from home.`
    },

    

    {
      type: "mc",
      q: "¿Por qué Ana enfatiza que la temperatura era de 'minus 20'?",
      options: [
        "Es un gran impacto para alguien de un país cálido",
        "Porque en realidad no hace frío",
        "Porque le encanta el frío extremo"
      ],
      a: "Es un gran impacto para alguien de un país cálido",
      successtip: "✓ CULTURE SHOCK!\nPasar de un clima tropical a -20°C es un cambio radical para cualquier inmigrante. 🥶"
    },
    {
      type: "mc",
      q: "En la frase 'I was freezing ALL the time', ¿por qué usa el pasado continuo?",
      options: [
        "Describe un estado continuo durante ese periodo",
        "Es un error del texto",
        "Solo pasó un segundo"
      ],
      a: "Describe un estado continuo durante ese periodo",
      successtip: "✓ ONGOING STATE!\nEl continuo ayuda a expresar que la sensación de frío era constante. ❄️"
    },
    {
      type: "mc",
      q: "¿Qué significa la frase de sus compañeros: 'Cold enough for you?'",
      options: [
        "Es una frase típica canadiense para romper el hielo (small talk)",
        "Es una pregunta seria sobre su preferencia exacta",
        "Es una burla pesada"
      ],
      a: "Es una frase típica canadiense para romper el hielo (small talk)",
      successtip: "✓ CANADIAN SMALL TALK!\nLos canadienses usan el clima para socializar constantemente. 💬"
    },
    {
      type: "mc",
      q: "La estructura 'TOO cold TO walk' significa que:",
      options: [
        "Hace demasiado frío para realizar la acción (no puede caminar)",
        "Ella quiere caminar en el frío",
        "El frío es perfecto para caminar"
      ],
      a: "Hace demasiado frío para realizar la acción (no puede caminar)",
      successtip: "✓ TOO... TO!\nEsta estructura indica un límite que impide realizar una actividad. 🚫🚶"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Winter Fatigue & Spring (preguntas 8, 9, 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Spring and Survival**

February was the worst. We call it "winter fatigue." But in April, the snow started melting. The temperature went from minus 10 to plus 10! I felt happy again! 

Canadian weather is extreme! It affects your mood, your clothes, your transportation - everything! But I survived my first Canadian winter! I'm proud of myself. Next winter, I'll be ready. I'm buying better boots!`
    },
    {
      type: "mc",
      q: "¿Qué es el 'winter fatigue' (fatiga de invierno)?",
      options: [
        "Un fenómeno real donde la gente se cansa del frío y la oscuridad en febrero",
        "Una enfermedad grave",
        "Un término que Ana inventó"
      ],
      a: "Un fenómeno real donde la gente se cansa del frío y la oscuridad en febrero",
      tip: "Winter fatigue is REAL! En febrero, casi todos en Canadá están listos para que el invierno termine. 💤",
      successtip: "✓ CULTURAL REALITY!\nEs un sentimiento compartido por casi todos los residentes de Canadá. 🇨🇦"
    },
    {
      type: "mc",
      q: "¿A qué temas de la vida diaria conecta Ana el clima en su texto?",
      options: [
        "A TODO: ropa, actividades, ánimo, transporte y trabajo",
        "Solo a la temperatura",
        "Solo a la nieve"
      ],
      a: "A TODO: ropa, actividades, ánimo, transporte y trabajo",
      successtip: "✓ TOTAL IMPACT!\nHas comprendido que en Canadá, el clima dicta cómo vivimos cada día. 🏠"
    },
    {
      type: "mc",
      q: "¿Cuál es la lección principal que Ana aprendió en su primer año?",
      options: [
        "El clima es extremo y afecta todo, pero puedes adaptarte y sobrevivir",
        "El clima en Canadá siempre es malo",
        "Es imposible sobrevivir al invierno"
      ],
      a: "El clima es extremo y afecta todo, pero puedes adaptarte y sobrevivir",
      successtip: "✓ READING COMPLETE! 🎉\n¡Felicidades! Has entendido la historia de resiliencia y adaptación de Ana. 🏆"
    }
  ]
},
  // SET 12: Unit Review
  reviewSet: {
    id: "m4-u12-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match structure to use:",
        pairs: [
          { left: "IT IS/IT'S + weather", right: "It's sunny / It's raining" },
          { left: "TOO + adjective + TO + verb", right: "Too cold to run" },
          { left: "WHEN/BECAUSE + weather + feeling", right: "I'm happy when it's sunny" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Question: 'What's the ___ like?'",
        options: ["weather", "whether", "wether"],
        a: "weather",
        tip: "'What's the weather LIKE?' = most common weather question!"
      },
      {
        type: "mc",
        q: "Temperature extremes: freezing = ___, boiling = ___",
        options: [
          "very cold, very hot",
          "cold, warm",
          "cool, hot"
        ],
        a: "very cold, very hot"
      },
      {
        type: "input",
        q: "Complete: 'It's snowing, ___ I'm wearing my winter coat.' (so/because)",
        a: "so / because"
      },
      {
        type: "input",
        q: "Complete: 'It's too hot ___ work outside.' (to/for)",
        a: "to"
      },
      {
        type: "order",
        q: "Ordena: 'Me siento feliz cuando hace sol'",
        words: ["I", "feel", "happy", "when", "it's", "sunny."],
        a: "I feel happy when it's sunny."
      },
      {
        type: "mc",
        q: "Canadian small talk starter:",
        options: [
          "'What's the weather like?' or 'Cold enough for you?'",
          "'How much money do you make?'",
          "'How old are you?'"
        ],
        a: "'What's the weather like?' or 'Cold enough for you?'"
      },
      {
        type: "mc",
        q: "'It's raining cats and dogs' means:",
        options: [
          "Raining very hard",
          "Animals falling",
          "Not raining"
        ],
        a: "Raining very hard"
      },
      {
        type: "mc",
        q: "ERROR: 'Is sunny today' should be:",
        options: [
          "'It's sunny today' (need IT)",
          "Correct as is",
          "'Sunny is today'"
        ],
        a: "'It's sunny today' (need IT)"
      },
      {
        type: "mc",
        q: "FINAL MASTERY: Real conversation at work:\n\nCoworker: 'Good morning! How's it going?'\nYou: 'Good! How are you?'\nCoworker: 'Not bad. What's the weather like today?'\nYou: 'It's sunny and warm - about 22 degrees!'\nCoworker: 'Oh perfect! I'm so happy when it's nice like this. Yesterday it was raining all day.'\nYou: 'I know! But the forecast says it's going to be sunny all week!'\nCoworker: 'Great! Maybe we can have lunch outside today?'\nYou: 'Yes! It's too nice to stay inside!'\n\nThis conversation uses:",
        options: [
          "ALL weather skills! Question (What's the weather like?), description (It's sunny and warm), temperature, feeling (I'm happy when), forecast (going to be sunny), activity decision (lunch outside), TOO structure = PERFECT natural Canadian small talk!",
          "Only weather words",
          "No grammar"
        ],
        a: "ALL weather skills! Question (What's the weather like?), description (It's sunny and warm), temperature, feeling (I'm happy when), forecast (going to be sunny), activity decision (lunch outside), TOO structure = PERFECT natural Canadian small talk!",
        successtip: "✓ UNIT 12 COMPLETE! Weather master! 🌟 From zero to natural Canadian weather conversations! You can now: describe any weather, use temperature words, connect weather to clothes/activities/feelings, understand idioms, make plans based on weather, handle small talk like a native! Weather vocabulary + present simple/continuous + connecting structures (too...to, when, because, so) = COMPLETE B1 level weather competency! This skill = essential Canadian integration! Weather talk = Canadian friendship builder! 🇨🇦☀️🌧️❄️🍁"
      }
    ]
  }
};
